import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { money, STATE_TONE, stateLabel } from './invoiceStatus';
import { useInvoicesBase } from './basePath';

export const LIST_TABS = [
  { id: 'needs_review', label: 'Needs review', states: ['needs_review'] },
  { id: 'awaiting_approval', label: 'Waiting for approval', states: ['awaiting_approval'] },
  { id: 'posted', label: 'Posted', states: ['auto', 'approved'] },
  { id: 'rejected', label: 'Rejected', states: ['rejected'] },
];

export function rowsForTab(byState, tabId) {
  const tab = LIST_TABS.find((t) => t.id === tabId) || LIST_TABS[0];
  const seen = new Set();
  return tab.states.flatMap((s) => byState[s] || []).filter((r) => !seen.has(r.id) && seen.add(r.id));
}

export function StatePill({ state }) {
  return (
    <span data-testid="invoice-state-pill" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATE_TONE[state] || STATE_TONE.rejected}`}>
      {stateLabel(state)}
    </span>
  );
}

export default function InvoiceList({ byState, tab, onTab, loaded, errors = {}, onRetry }) {
  const rows = rowsForTab(byState, tab);
  const failed = Object.keys(errors).length > 0;
  const tabFailed = (LIST_TABS.find((t) => t.id === tab) || LIST_TABS[0]).states.some((s) => errors[s]);
  const base = useInvoicesBase();
  return (
    <section data-testid="invoice-list">
      <div role="tablist" aria-label="Invoice status" className="flex gap-2 overflow-x-auto pb-2">
        {LIST_TABS.map((t) => {
          const count = rowsForTab(byState, t.id).length;
          const sel = t.id === tab;
          return (
            <button key={t.id} type="button" role="tab" aria-selected={sel} data-testid={`invoice-tab-${t.id}`}
              onClick={() => onTab(t.id)}
              className={`shrink-0 min-h-[44px] px-4 rounded-full text-sm font-medium border motion-organic ${sel ? 'bg-primary-500 text-surface border-primary-500' : 'bg-surface text-ink-muted border-surface-border hover:text-primary-600'}`}>
              {t.label} <span className={`ml-1 ${sel ? 'text-primary-100' : 'text-ink-subtle'}`}>{t.states.some((s) => errors[s]) ? `${count} (stale / unavailable)` : count}</span>
            </button>
          );
        })}
      </div>
      {failed && <div role="alert" data-testid="invoice-list-error" className="text-sm text-danger py-3">
        Could not refresh all invoice lists. Results may be partial or stale; unavailable lists are not empty.
        {onRetry && <button type="button" onClick={onRetry} className="ml-2 underline">Retry</button>}
      </div>}
      {loaded && !tabFailed && rows.length === 0 && (
        <p className="text-sm text-ink-muted py-8 text-center" data-testid="invoice-list-empty">Nothing here.</p>
      )}
      {rows.length > 0 && (
      <ul className="divide-y divide-surface-border rounded-2xl border border-surface-border bg-surface overflow-hidden">
        {rows.map((inv) => (
          <li key={inv.id}>
            <Link to={`${base}/${inv.id}`} data-testid={`invoice-row-${inv.id}`}
              className="flex items-center gap-4 px-4 py-4 min-h-[64px] hover:bg-primary-50 focus:bg-primary-50 motion-organic">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink truncate">{inv.supplier_name || 'Supplier not read yet'}</div>
                <div className="text-sm text-ink-muted">
                  {inv.invoice_no || 'No invoice number'}{inv.date ? ` · ${inv.date}` : ''}
                </div>
                {inv.duplicate_of && (
                  <div className="text-xs text-accent-800 mt-0.5" data-testid={`invoice-resend-${inv.id}`}>
                    Looks like a re-send of an invoice already received
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="font-mono text-ink">{money(inv.invoice_total)}</div>
                <StatePill state={inv.state} />
              </div>
              <ChevronRight className="w-5 h-5 text-ink-subtle shrink-0" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
      )}
    </section>
  );
}
